import { matchPath } from 'react-router';

import {
  type AgentCorePageContext,
  type AgentThreadSubject,
} from '@/shared/api/agentCore/agentCoreApi';

type PageResource = AgentCorePageContext['resources'][number];

type ResolvedPage = {
  page: string;
  resources: PageResource[];
  /** Entity this page's chat is bound to; absent = non-entity page. */
  subject?: AgentThreadSubject;
};

type RouteParams = Record<string, string | undefined>;

type PageContextRule = {
  /** Absolute react-router pattern; rules are exact matches (no trailing wildcard). */
  pattern: string;
  resolve: (params: RouteParams, search: URLSearchParams) => ResolvedPage;
};

const resource = (
  type: string,
  id: string | null | undefined,
): PageResource | undefined => (id ? { type, id } : undefined);

const resources = (...items: (PageResource | undefined)[]): PageResource[] =>
  items.filter((item): item is PageResource => Boolean(item));

const reportPage = (
  params: RouteParams,
  ...extra: (PageResource | undefined)[]
): ResolvedPage => ({
  page: 'patient-report',
  resources: resources(resource('report', params.reportID), ...extra),
  subject: subjectOrUndefined('report', params.reportID),
});

const subjectOrUndefined = (
  type: AgentThreadSubject['type'],
  id: string | null | undefined,
): AgentThreadSubject | undefined => (id ? { type, id } : undefined);

// Report routes whose first dynamic segment is a report kind, not a report
// ID: /patients/:id/reports/<kind>/<reportID>.
const REPORT_KINDS = [
  'endodontic',
  'implant',
  'molar',
  'segmentation',
] as const;

// Report sub-pages that all describe the same base report resource.
const REPORT_SECTIONS = [
  'gp',
  'pano',
  'bitewings',
  'ioxray',
  'preview',
] as const;

/**
 * Ordered most-specific-first. Every mounted protected route in
 * `app/model/router.tsx` is covered; unstructured top-level pages fall
 * through to the pathname-derived key in `resolveAgentPageContext`.
 */
const PAGE_CONTEXT_RULES: PageContextRule[] = [
  // Transcriptor consultation page: the consultation ID rides in the query
  // string (?id=...). The ?tab=... value is a generated-document section
  // (summary, evaluation, ...) — a view detail, not a resource.
  {
    pattern: '/patients/:patientID/reports/consultation',
    resolve: (_params, search) => ({
      page: 'patient-consultation',
      resources: resources(resource('consultation', search.get('id'))),
      // A Transcriptor consultation is the same entity as a scribe
      // encounter (consultation_id == encounter_id in the API).
      subject: subjectOrUndefined('encounter', search.get('id')),
    }),
  },
  // Dental photo gallery — no single resource to focus on, but the chat
  // still belongs to the patient.
  {
    pattern: '/patients/:patientID/reports/dentalPhotos',
    resolve: (params) => ({
      page: 'patient-dental-photos',
      resources: [],
      subject: subjectOrUndefined('patient', params.patientID),
    }),
  },
  {
    pattern: '/patients/:patientID/reports/:reportID/gp/tooth/:toothID',
    resolve: (params) => reportPage(params, resource('tooth', params.toothID)),
  },
  ...REPORT_SECTIONS.map((section) => ({
    pattern: `/patients/:patientID/reports/:reportID/${section}`,
    resolve: (params: RouteParams) => reportPage(params),
  })),
  ...REPORT_KINDS.map((kind) => ({
    pattern: `/patients/:patientID/reports/${kind}/:reportID`,
    resolve: (params: RouteParams) => reportPage(params),
  })),
  // Standalone 2D report preview (sibling of PatientLayout).
  {
    pattern: '/patients/:patientID/preview2d/:reportID',
    resolve: (params) => reportPage(params),
  },
  // Base report page.
  {
    pattern: '/patients/:patientID/reports/:reportID',
    resolve: (params) => reportPage(params),
  },
  // Patient document editor. The floating chat is disabled on this route
  // (the page embeds its own document-scoped chat); the mapping is kept
  // here as the single source of truth for "what page is this".
  {
    pattern: '/patients/:patientID/documents/:documentID',
    resolve: (params) => ({
      page: 'patient-document',
      resources: resources(resource('document', params.documentID)),
      subject: subjectOrUndefined('document', params.documentID),
    }),
  },
  // Patient profile — the patient is already bound to the thread scope.
  {
    pattern: '/patients/:patientID',
    resolve: (params) => ({
      page: 'patient',
      resources: [],
      subject: subjectOrUndefined('patient', params.patientID),
    }),
  },
  // Company settings: the Transcriptor template editors carry editor
  // resources in the query string; other tabs are a plain settings page.
  {
    pattern: '/company-settings',
    resolve: (_params, search) => {
      if (search.get('tab') !== 'Templates') {
        return { page: 'company-settings', resources: [] };
      }
      const editor = search.get('editor');
      const editorID = search.get('id');
      return {
        page: 'transcriptor-templates',
        resources: resources(
          editor === 'document'
            ? resource('document-template', editorID)
            : undefined,
          editor === 'schema'
            ? resource('encounter-schema', editorID)
            : undefined,
          resource(
            'document-template-series',
            search.get('imported_template_series_id'),
          ),
        ),
      };
    },
  },
  {
    pattern: '/encounters/:encounterId',
    resolve: (params) => ({
      page: 'encounter',
      resources: resources(resource('encounter', params.encounterId)),
      subject: subjectOrUndefined('encounter', params.encounterId),
    }),
  },
  {
    pattern: '/encounters',
    resolve: () => ({ page: 'encounters', resources: [] }),
  },
];

/**
 * Resolves the agent page context (page key + focused resource IDs) for the
 * current location. Always returns a context: unmatched structured routes
 * degrade to a pathname-derived page key with no resources.
 */
export const resolveAgentPageContext = (
  pathname: string,
  search: string,
): AgentCorePageContext => {
  const params = new URLSearchParams(search);
  for (const rule of PAGE_CONTEXT_RULES) {
    const match = matchPath(rule.pattern, pathname);
    if (match) {
      return { ...rule.resolve(match.params, params), path: pathname };
    }
  }
  return {
    page: pathname.split('/').filter(Boolean)[0] || 'patients',
    resources: [],
    path: pathname,
  };
};
